/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { getDefaultStore } from 'jotai';
import { DefaultWorkspaceResponse, OriginType, SanctioningBodyEnum, UserPreferenceResponse } from 'models/viewModels';
import { UserPreferencesObject, userPreferencesAtom, userPreferencesStringAtom } from 'jotai/userPreferences';
import { QtmThemeSetting } from 'theme';
import { userPreferencesApi } from './UserPreferencesApi';
import { getUserPreferencesDefault, upgrade } from './UserPreferencesFactory';
import { LockedWorkspaceId, SystemWorkspaceId, WorkspaceObject } from 'jotai/workspace';
import { systemWorkspaceService } from 'services/workspace/SystemWorkspaceService';
import { lockedWorkspaceService } from 'services/workspace/LockedWorkspaceService';

const defaultStore = getDefaultStore();

class UserPreferencesService {
  cachePromise: Promise<string> | undefined;

  private getCache(): Promise<string> {
    if (this.cachePromise) {
      return this.cachePromise;
    }
    this.cachePromise = this.getUserPreferences().then((result) => {
      defaultStore.set(userPreferencesStringAtom, result);
      defaultStore.set(userPreferencesAtom, JSON.parse(result));
      return result;
    });
    return this.cachePromise;
  }

  private async getUserPreferences(): Promise<string> {
    return userPreferencesApi.getUserPreferences().then((response: UserPreferenceResponse) => {
      const data = response.data;
      return JSON.stringify(data ? upgrade(JSON.parse(data)) : getUserPreferencesDefault());
    });
  }

  private async createUserPreferences(): Promise<UserPreferencesObject> {
    const cache = await this.getCache();
    return JSON.parse(cache) as UserPreferencesObject;
  }

  async updateUserPreferences(userPreferences: UserPreferencesObject): Promise<boolean> {
    const status = await userPreferencesApi.updateUserPreferences(userPreferences).then((response) => response.status);
    if (status) {
      this.cachePromise = Promise.resolve(JSON.stringify(userPreferences));
    }
    return status;
  }

  async getColorMode(): Promise<QtmThemeSetting> {
    return await this.createUserPreferences().then((result) => result.colorMode);
  }

  async setColorMode(mode: QtmThemeSetting) {
    const userPreferences = await this.createUserPreferences();
    userPreferences.colorMode = mode;
    this.updateUserPreferences(userPreferences);
  }

  async getAutoSaveWorkspace(): Promise<boolean> {
    return await this.createUserPreferences().then((result) => result.autoSaveWorkspace);
  }

  async setAutoSaveWorkspace(setting: boolean) {
    const userPreferences = await this.createUserPreferences();
    userPreferences.autoSaveWorkspace = setting;
    this.updateUserPreferences(userPreferences);
  }

  async getDefaultSanctioningBody(): Promise<string | undefined> {
    return await this.createUserPreferences().then((result) => result.defaultSanctioningBody);
  }

  async setDefaultSanctioningBody(id?: string, feedSource?: OriginType): Promise<boolean> {
    const userPreferences = await this.createUserPreferences();
    userPreferences.defaultSanctioningBody = id;
    userPreferences.defaultSanctioningBodyFeed = feedSource;
    return this.updateUserPreferences(userPreferences);
  }

  async getDataFeedMonitor(): Promise<boolean> {
    return await this.createUserPreferences().then((result) => result.dataFeedMonitor);
  }

  async setDataFeedMonitor(setting: boolean) {
    const userPreferences = await this.createUserPreferences();
    userPreferences.dataFeedMonitor = setting;
    this.updateUserPreferences(userPreferences);
  }

  async getDefaultSanctioningBodyFeed(): Promise<OriginType | undefined> {
    return await this.createUserPreferences().then((result) => result.defaultSanctioningBodyFeed);
  }

  async getSanctioningBodyDefaultWorkspaceId(sanctioningBodyId: SanctioningBodyEnum): Promise<string> {
    return await this.createUserPreferences().then(
      (result) => result.sanctioningBodyDefaultWorkspaces[sanctioningBodyId]
    );
  }

  async getSanctioningBodyDefaultWorkspaces(): Promise<DefaultWorkspaceResponse> {
    return userPreferencesApi.getDefaultWorkspaces();
  }

  async getSanctioningBodyDefaultWorkspace(sanctioningBody: SanctioningBodyEnum): Promise<WorkspaceObject | undefined> {
    const userPreferencesWorkspace = await userPreferencesApi.getDefaultWorkspace(sanctioningBody);
    let workspaceId = '';
    if (userPreferencesWorkspace) {
      if (userPreferencesWorkspace?.data?.length > 0) {
        return JSON.parse(userPreferencesWorkspace.data);
        // if the workspace doesn't have data it's because it is either a locked or system workspace so it is not in the DB
      } else {
        workspaceId = userPreferencesWorkspace.id;
      }
    } else {
      // if the user hasn't been on the site in awhile, then we need to upgrade their userPreferences to the latest version
      const userPreferences = await this.createUserPreferences();
      workspaceId = userPreferences.sanctioningBodyDefaultWorkspaces[sanctioningBody];
    }
    if (systemWorkspaceService.getWorkspaceIds().includes(workspaceId)) {
      return systemWorkspaceService.getWorkspace(workspaceId as SystemWorkspaceId);
    } else if (lockedWorkspaceService.getWorkspaceIds().includes(workspaceId as LockedWorkspaceId)) {
      return lockedWorkspaceService.getWorkspace(workspaceId as LockedWorkspaceId);
    }
  }

  async updateSanctioningBodyDefaultWorkspaceId(sanctioningBodyId: SanctioningBodyEnum, workspaceId: string) {
    const userPreferences = await this.createUserPreferences();
    userPreferences.sanctioningBodyDefaultWorkspaces[sanctioningBodyId] = workspaceId;
    this.updateUserPreferences(userPreferences);
  }
}

export const userPreferencesService = new UserPreferencesService();
